import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Link } from 'react-router-dom';

import CircularProgress from '@material-ui/core/CircularProgress';
import GestureIcon from '@material-ui/icons/Gesture';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenRoundedIcon from '@material-ui/icons/LockOpenRounded';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import RecordVoiceOverRoundedIcon from '@material-ui/icons/RecordVoiceOverRounded';

import GeocoderService from '../../../services/GeocoderService';

import { deviceBattery, getMotionDescription, lastSeenDescription } from '../../../services/DeviceUtils';
import DeviceImage from '../DeviceImage.jsx';

import '../../../../stylesheets/components/device_summary_panel.scss';
import { DeviceBatteryIndicator } from '../../bits/DeviceBatteryIndicator';

import { customFetchV2 } from '../../../services/custom-fetch-v2.ts';
import { deviceActivityState } from '../../../services/device-utils-v2.ts';

function ActionItem(props) {
  return (
    <div className="action-item" {...props}>
      {props.icon}
      {props.title}
    </div>
  );
}

class DeviceLockActionItem extends Component {
  state = {
    isLoading: false,
  };

  handleClick = () => {
    const { isLocked, onClick } = this.props;

    this.setState({ isLoading: true });
    onClick(!isLocked).then(() => {
      this.setState({ isLoading: false });
    });
  };

  render() {
    const { isLocked } = this.props;

    if (this.state.isLoading) {
      return (
        <button disabled={true} className="std-button">
          <CircularProgress color="inherit" size={22}/>
        </button>
      );
    }

    return (
      <button onClick={this.handleClick} className="std-button">
        {isLocked
          ? <ActionItem key="lock-link" icon={<LockIcon/>} title="Unlock" style={{ color: '#ff0045' }}/>
          : <ActionItem key="lock-link" icon={<LockOpenRoundedIcon/>} title="Lock"/>
        }
      </button>
    );
  }
}

class DeviceSummaryPanel extends Component {
  state = {};

  geocoderService = null;

  setDeviceLock = (locked) => {
    const deviceId = this.props.device.id;

    return customFetchV2(`/api/v2/devices/${deviceId}/location-lock`, {
      method: 'POST',
      delay: 150,
      body: JSON.stringify({ locked }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to lock/unlock - HTTP response ${response.status}`);
        }
      })
      .then(this.props.onDeviceRefresh)
      .catch((error) => {
        console.error("Unable to lock/unlock device");
        console.error(error.message);
      });
  };


  renderDeviceActions = (device) => {
    const { isLocationLocked } = this.props.device;
    const actions = [];

    // TODO: Remove feature flag
    actions.push(
      <DeviceLockActionItem
        key="lock-link"
        isLocked={isLocationLocked}
        onClick={this.setDeviceLock}
      />,
    );

    if (device.audioEnabled) {
      actions.push(
        <a key="call-link" href={`tel:${device.phoneNumber}`}>
          <ActionItem icon={<RecordVoiceOverRoundedIcon/>} title="Call"/>
        </a>,
      );
    }

    // TODO: Use device.features
    if (['GV500MA', 'FMT100', 'G1C', 'GTU30', 'GTU70'].includes(device.model)) {
      actions.push(
        <Link key="trips-link" to={{
          pathname: '/m/trips',
          search: `?device-filter=${device.id}`,
          state: { origin: 'dashboard' },
        }}>
          <ActionItem icon={<GestureIcon/>} title="Trips"/>
        </Link>,
      );
    }

    actions.push(
      <Link key="more-link" to={{
        pathname: `/m/devices/${device.id}`,
        state: { origin: 'dashboard' },
      }}>
        <ActionItem icon={<MoreHorizIcon/>} title="More"/>
      </Link>,
    );

    return actions;
  }

  componentDidMount() {
    this.geocoderService = new GeocoderService();
  }

  componentDidUpdate(prevProps) {
    if (this.props.device !== prevProps.device) {
      // If the device has changed then lets reset, to avoid stale addresses
      // showing in the panel
      this.setState({ deviceAddress: null });
    }

    if (this.props.device && this.props.device !== prevProps.device) {
      const { position } = this.props.device;
      const activityState = deviceActivityState(this.props.device);

      if (activityState !== 'moving' && position) {
        this.geocoderService.reverseGeocodeWithCache(position.latitude, position.longitude)
          .then((result) => {
            this.setState({ deviceAddress: result.address });
          });
      }
    }
  }

  renderSummaryPanel() {
    const { device } = this.props;
    const activityState = deviceActivityState(device);
    const battery = deviceBattery(device);

    return (
      <div className="summary-panel">
        <div className="summary-panel__info">
          <div className="summary-panel__info__photo">
            <DeviceImage device={device} size="medium" />
          </div>
          <div className="summary-panel__info__details">
            <div className="summary-panel__info__details__top">
              <div className="summary-panel__top-1">
                <div className="summary-panel__top-1__title">{device.name}</div>
                <div className="summary-panel__top-1__subtitle">{lastSeenDescription(device)}</div>
              </div>
              <div className="summary-panel__top-3">
                {battery != null ? (
                    <div className="summary-panel__battery">
                      <DeviceBatteryIndicator device={device}/>
                    </div>
                ) : null}
              </div>
            </div>
            <div className="text text--small">
              { activityState === 'moving' ? (
                <p>{getMotionDescription(device)}</p>
              ) : (
                <p>{this.state.deviceAddress}</p>
              )}
            </div>
          </div>
        </div>
        <div className="summary-panel__actions">
          {this.renderDeviceActions(device)}
        </div>
      </div>
    );
  }

  render() {
    return (
      <div id="device-summary-panel" className="test-window">
        {this.props.device ? this.renderSummaryPanel() : null}
      </div>
    );
  }
}

DeviceSummaryPanel.propTypes = {
  // Device prop is optional, only when a value is specified do we render the
  // component.
  device: PropTypes.object,
  user: PropTypes.object,
  onDeviceRefresh: PropTypes.func,
};

export default DeviceSummaryPanel;
